import React, { useMemo, useRef, useState } from "react";
import { DesktopShell } from "./desktopShell";
import {
  Btn,
  Chip,
  DateField,
  PaceSlider,
  RulerInput,
  SegmentedToggle,
  SelectableCard,
  WheelPicker,
} from "./components";
import { PreviewBMR, PreviewIntro, PreviewProjection, PreviewTDEE } from "./previewPane";
import { GOAL_ICONS } from "./screens/GoalScreen";
import { T } from "./theme";
import {
  ACTIVITY_OPTIONS,
  autoSprintName,
  bmrFor,
  cautionPaceForGoal,
  chartEndpointsForPlan,
  dailyTargetFor,
  defaultPaceLbsPerWeek,
  effectivePaceLbsPerWeek,
  maxPaceForGoal,
  pacePresetsForGoal,
  projectedEndDateYmd,
  tdeeFor,
} from "./calc";
import { cmToIn, displayWeight, inToCm, parseWeightToLbs, weightMax, weightMin, weightStep } from "./units";
import type {
  ActivityKey,
  AuthChoice,
  GoalDirection,
  HeightUnit,
  OnboardingDraft,
  PacePreset,
  Sex,
  WeightUnit,
} from "./types";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DESKTOP_STEPS = [
  { id: "goal", label: "Goal" },
  { id: "basics", label: "Basics" },
  { id: "activity", label: "Activity" },
  { id: "target", label: "Target" },
  { id: "plan", label: "Plan" },
];

const GOAL_LABELS: Record<GoalDirection, string> = {
  lose: "Lose weight",
  gain: "Build muscle",
  maintain: "Maintain",
};

function inferGoalFromDelta(deltaLbs: number): GoalDirection {
  if (Math.abs(deltaLbs) < 0.5) return "maintain";
  return deltaLbs < 0 ? "lose" : "gain";
}

function daysInMonth(year: number, monthIdx: number): number {
  return new Date(year, monthIdx + 1, 0).getDate();
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function fmtDate(ymd: string): string {
  const d = new Date(ymd + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export type DesktopFlowProps = {
  draft: OnboardingDraft;
  setDraft: React.Dispatch<React.SetStateAction<OnboardingDraft>>;
  /** Effective direction — derived from delta. Used everywhere except the
   * mismatch-warning chip on the Target step. */
  goal: GoalDirection;
  /** The user's original choice on the Goal screen — used only by the Target
   * step warning chip to detect a delta-vs-intent mismatch. */
  storedGoal: GoalDirection | null;
  /** Already-authed users skip the sign-in column on the plan step. */
  isAuthenticated: boolean;
  onComplete: (choice: AuthChoice) => Promise<void> | void;
  onSignIn?: () => void;
  onExit?: () => void;
};

export function DesktopFlow({ draft, setDraft, goal, storedGoal, isAuthenticated, onComplete, onSignIn, onExit }: DesktopFlowProps): React.ReactElement {
  const [stepIndex, setStepIndex] = useState(0);
  const [maxVisited, setMaxVisited] = useState(0);

  function setField<K extends keyof OnboardingDraft>(key: K, value: OnboardingDraft[K]): void {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  function next(): void {
    setStepIndex((i) => {
      const n = Math.min(DESKTOP_STEPS.length - 1, i + 1);
      setMaxVisited((prev) => Math.max(prev, n));
      return n;
    });
  }
  function back(): void {
    setStepIndex((i) => Math.max(0, i - 1));
  }
  function jumpTo(i: number): void {
    if (i < 0 || i >= DESKTOP_STEPS.length) return;
    if (i > maxVisited) return;
    setStepIndex(i);
  }
  const isStepReachable = (i: number) => i <= maxVisited;

  // Surfaced as `headerRight` on every step except the final plan/auth step.
  // Lets a returning user who accidentally landed in onboarding jump to the
  // existing SignInModal without losing their place — see plan doc for context.
  const signInNode = onSignIn && !isAuthenticated ? (
    <Btn kind="ghost" size="sm" onClick={onSignIn}>
      Sign in
    </Btn>
  ) : null;

  if (stepIndex === 0) {
    return (
      <DesktopShell
        steps={DESKTOP_STEPS}
        currentStepIndex={0}
        onStepClick={jumpTo}
        isStepReachable={isStepReachable}
        eyebrow="Step 01 · Goal"
        title={<>What are you here to do?</>}
        subtitle="Pick the one that fits the next 6–12 weeks. You can change direction between sprints."
        onBack={null}
        onPrimary={next}
        primaryLabel="Continue"
        primaryDisabled={draft.goalDirection === null}

        onExit={onExit}
        headerRight={signInNode}
        preview={<PreviewIntro picked={draft.goalDirection} />}
        form={
          <div style={{ display: "grid", gap: 12, maxWidth: 560 }}>
            <SelectableCard
              selected={draft.goalDirection === "lose"}
              onClick={() => setField("goalDirection", "lose")}
              icon={GOAL_ICONS.lose}
              title="Lose weight"
              body="Cut phase. You'll get a daily calorie target below your TDEE."
            />
            <SelectableCard
              selected={draft.goalDirection === "maintain"}
              onClick={() => setField("goalDirection", "maintain")}
              icon={GOAL_ICONS.maintain}
              title="Maintain"
              body="Hold your current weight. Daily target matches your TDEE."
            />
            <SelectableCard
              selected={draft.goalDirection === "gain"}
              onClick={() => setField("goalDirection", "gain")}
              icon={GOAL_ICONS.gain}
              title="Build muscle"
              body="Lean bulk. Your target sits above TDEE in a controlled surplus."
            />
          </div>
        }
      />
    );
  }

  if (stepIndex === 1) {
    return (
      <DesktopBasicsStep
        draft={draft}
        setField={setField}
        onBack={back}
        onNext={next}
        onExit={onExit}
        headerRight={signInNode}
        onStepClick={jumpTo}
        isStepReachable={isStepReachable}
      />
    );
  }

  if (stepIndex === 2) {
    return (
      <DesktopShell
        steps={DESKTOP_STEPS}
        currentStepIndex={2}
        onStepClick={jumpTo}
        isStepReachable={isStepReachable}
        eyebrow="Step 03 · Activity"
        title={<>How hard are you training?</>}
        subtitle="Outside of intentional workouts — your typical day. We recalibrate from your data after the first week."
        onBack={back}
        backLabel="Basics"
        onPrimary={next}
        primaryLabel="Continue"

        onExit={onExit}
        headerRight={signInNode}
        preview={<PreviewTDEE draft={draft} />}
        form={
          <div style={{ display: "grid", gap: 10, maxWidth: 580 }}>
            {ACTIVITY_OPTIONS.map((opt) => (
              <SelectableCard
                key={opt.key}
                selected={draft.activityKey === opt.key}
                onClick={() => {
                  setDraft((d) => ({ ...d, activityKey: opt.key as ActivityKey, activityFactor: opt.factor }));
                }}
                icon={
                  <span
                    style={{
                      fontWeight: 800,
                      fontSize: "0.82rem",
                      letterSpacing: "-0.01em",
                      ...T.tabular,
                    }}
                  >
                    ×{opt.factor.toFixed(2)}
                  </span>
                }
                title={opt.label}
                body={opt.example}
              />
            ))}
          </div>
        }
      />
    );
  }

  if (stepIndex === 3) {
    return (
      <DesktopTargetStep
        draft={draft}
        setDraft={setDraft}
        setField={setField}
        goal={goal}
        storedGoal={storedGoal}
        onBack={back}
        onNext={next}
        onExit={onExit}
        headerRight={signInNode}
        onStepClick={jumpTo}
        isStepReachable={isStepReachable}
      />
    );
  }

  // Step 5 — plan reveal + sign-in on the same page (no right preview)
  return (
    <DesktopPlanAndAuthStep
      draft={draft}
      goal={goal}
      isAuthenticated={isAuthenticated}
      onBack={back}
      onComplete={onComplete}
      onExit={onExit}
      onStepClick={jumpTo}
      isStepReachable={isStepReachable}
    />
  );
}

// ── Step 02 — Basics: sex + DOB + height on one page ────────────────────

function DesktopBasicsStep({
  draft,
  setField,
  onBack,
  onNext,
  onExit,
  headerRight,
  onStepClick,
  isStepReachable,
}: {
  draft: OnboardingDraft;
  setField: <K extends keyof OnboardingDraft>(k: K, v: OnboardingDraft[K]) => void;
  onBack: () => void;
  onNext: () => void;
  onExit?: () => void;
  headerRight?: React.ReactNode;
  onStepClick?: (i: number) => void;
  isStepReachable?: (i: number) => boolean;
}): React.ReactElement {
  const now = new Date();
  const maxYear = now.getFullYear() - 13;
  const minYear = 1925;

  const requiredOk = !!draft.sex && /^\d{4}-\d{2}-\d{2}$/.test(draft.birthday) && draft.heightIn > 0;

  return (
    <DesktopShell
      steps={DESKTOP_STEPS}
      currentStepIndex={1}
      onStepClick={onStepClick}
      isStepReachable={isStepReachable}
      eyebrow="Step 02 · Basics"
      title={<>About you.</>}
      subtitle="We use these to estimate your resting burn. Stays on-device until you sign in."
      onBack={onBack}
      backLabel="Goal"
      onPrimary={onNext}
      primaryLabel="Continue"
      primaryDisabled={!requiredOk}

      onExit={onExit}
      headerRight={headerRight}
      preview={<PreviewBMR draft={draft} />}
      form={
        <div style={{ display: "grid", gap: 20, maxWidth: 580 }}>
          {/* Sex */}
          <div>
            <Eyebrow>Biological sex</Eyebrow>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <SegmentedToggle
                options={[
                  { value: "M", label: "Male" },
                  { value: "F", label: "Female" },
                  { value: "X", label: "Prefer not to say" },
                ]}
                value={draft.sex}
                onChange={(v) => setField("sex", v as Sex)}
              />
            </div>
          </div>

          {/* DOB — custom calendar popover */}
          <div>
            <Eyebrow>Date of birth</Eyebrow>
            <div style={{ marginTop: 10 }}>
              <DateField
                value={draft.birthday}
                min={`${minYear}-01-01`}
                max={`${maxYear}-12-31`}
                onChange={(v) => setField("birthday", v)}
                placeholder="Pick your birthday"
              />
            </div>
          </div>

          {/* Height */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Eyebrow>Height</Eyebrow>
              <SegmentedToggle
                size="sm"
                options={[
                  { value: "in", label: "ft / in" },
                  { value: "cm", label: "cm" },
                ]}
                value={draft.unitsHeight}
                onChange={(u) => setField("unitsHeight", u as HeightUnit)}
              />
            </div>
            <div style={{ marginTop: 10 }}>
              {draft.unitsHeight === "in" ? (
                <>
                  <div style={{ textAlign: "center", fontSize: "1.9rem", fontWeight: 700, letterSpacing: "-0.03em", ...T.tabular, lineHeight: 1 }}>
                    {Math.floor(draft.heightIn / 12)}
                    <span style={{ fontSize: "0.84rem", color: T.muted, fontWeight: 700, marginLeft: 6 }}>ft</span>
                    <span style={{ marginLeft: 14 }}>{Math.round((draft.heightIn - Math.floor(draft.heightIn / 12) * 12) * 10) / 10}</span>
                    <span style={{ fontSize: "0.84rem", color: T.muted, fontWeight: 700, marginLeft: 6 }}>in</span>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <RulerInput
                      value={draft.heightIn}
                      min={48}
                      max={84}
                      step={0.5}
                      majorStep={6}
                      unit="in"
                      decimals={1}
                      accent="blue"
                      hideValue
                      onChange={(v) => setField("heightIn", v)}
                    />
                  </div>
                </>
              ) : (
                <RulerInput
                  value={Math.round(inToCm(draft.heightIn))}
                  min={120}
                  max={220}
                  step={1}
                  majorStep={10}
                  unit="cm"
                  accent="blue"
                  onChange={(cm) => setField("heightIn", +cmToIn(cm).toFixed(2))}
                />
              )}
            </div>
          </div>
        </div>
      }
    />
  );
}

// ── Step 04 — Target: current + target + pace on one page ────────────────

function DesktopTargetStep({
  draft,
  setDraft,
  setField,
  goal,
  storedGoal,
  onBack,
  onNext,
  onExit,
  headerRight,
  onStepClick,
  isStepReachable,
}: {
  draft: OnboardingDraft;
  setDraft: React.Dispatch<React.SetStateAction<OnboardingDraft>>;
  setField: <K extends keyof OnboardingDraft>(k: K, v: OnboardingDraft[K]) => void;
  goal: GoalDirection;
  storedGoal: GoalDirection | null;
  onBack: () => void;
  onNext: () => void;
  onExit?: () => void;
  headerRight?: React.ReactNode;
  onStepClick?: (i: number) => void;
  isStepReachable?: (i: number) => boolean;
}): React.ReactElement {
  const [mode, setMode] = useState<"pace" | "date">("pace");
  const currentDisp = displayWeight(draft.currentWeightLbs, draft.unitsWeight);
  const targetDisp = displayWeight(draft.targetWeightLbs, draft.unitsWeight);

  const seedByUnit = useRef<{ lb: number | null; kg: number | null }>({ lb: null, kg: null });
  if (seedByUnit.current[draft.unitsWeight] === null) {
    seedByUnit.current[draft.unitsWeight] = currentDisp;
  }
  const seed = seedByUnit.current[draft.unitsWeight] ?? currentDisp;
  const range = useMemo(() => {
    const span = draft.unitsWeight === "lb" ? 80 : 35;
    const minV = Math.max(weightMin(draft.unitsWeight), Math.round(seed - span));
    const maxV = Math.min(weightMax(draft.unitsWeight), Math.round(seed + span));
    return { min: minV, max: maxV };
  }, [draft.unitsWeight, seed]);

  const delta = draft.targetWeightLbs - draft.currentWeightLbs;
  const inferred = inferGoalFromDelta(delta);
  // Compare the stored intent (what the user picked on the goal screen) to
  // the effective direction (delta sign). `goal` is now always the effective
  // one, so we can't use it for mismatch detection — we need the stored value.
  const mismatch =
    storedGoal !== null && storedGoal !== "maintain" && inferred !== "maintain" && storedGoal !== inferred;

  const computedEndYmd = useMemo(() => {
    if (goal === "maintain") return "";
    const sex = draft.sex ?? "X";
    const bmr = bmrFor({ weightLbs: draft.currentWeightLbs, heightIn: draft.heightIn, sex, birthday: draft.birthday }) ?? 0;
    const tdee = bmr > 0 ? tdeeFor(bmr, draft.activityFactor) : 0;
    const effPace = tdee > 0
      ? effectivePaceLbsPerWeek({ paceLbsPerWeekAbs: draft.paceLbsPerWeekAbs, tdee, sex, goal })
      : draft.paceLbsPerWeekAbs;
    return projectedEndDateYmd(draft.currentWeightLbs, draft.targetWeightLbs, effPace);
  }, [
    goal,
    draft.sex,
    draft.birthday,
    draft.heightIn,
    draft.activityFactor,
    draft.currentWeightLbs,
    draft.targetWeightLbs,
    draft.paceLbsPerWeekAbs,
  ]);

  const todayStr = new Date().toISOString().slice(0, 10);
  const minDate = new Date(); minDate.setDate(minDate.getDate() + 21);
  const maxDate = new Date(); maxDate.setDate(maxDate.getDate() + 730);
  const minYmd = minDate.toISOString().slice(0, 10);
  const maxYmd = maxDate.toISOString().slice(0, 10);

  function setPace(lbsPerWeek: number, preset?: PacePreset): void {
    setDraft((d) => ({
      ...d,
      paceLbsPerWeekAbs: lbsPerWeek,
      pacePreset: preset ?? d.pacePreset,
    }));
  }
  const maxPace = maxPaceForGoal(goal);
  const cautionPace = cautionPaceForGoal(goal);
  const presets = pacePresetsForGoal(goal);
  const [dateYmdLocal, setDateYmdLocal] = useState<string>("");

  function setEndDate(ymd: string): void {
    setDateYmdLocal(ymd);
    const dt = new Date(ymd + "T00:00:00");
    const todayDate = new Date();
    const days = Math.max(1, Math.round((dt.getTime() - todayDate.getTime()) / 86400000));
    const deltaAbs = Math.abs(draft.targetWeightLbs - draft.currentWeightLbs);
    // Keep full precision — rounding to 2 decimals here causes the stored
    // pace to project to a different end date than the one picked, which
    // then triggers the dateClamp warning for non-safety reasons.
    const lbsPerWeek = days > 0 ? deltaAbs / (days / 7) : 0;
    const clamped = Math.min(maxPace, Math.max(0.1, lbsPerWeek));
    setPace(clamped);
  }

  // Fires only when the picked date genuinely requires a pace above the safe
  // maxPace cap — that's the one case where the "below the safety floor"
  // warning is correct. The old comparison of computedEnd vs dateYmd with a
  // ±2 day tolerance fired spuriously on rounding drift.
  const dateClamp = mode === "date" && goal !== "maintain" && dateYmdLocal
    ? (() => {
        const dt = new Date(dateYmdLocal + "T00:00:00");
        const td = new Date();
        td.setHours(0, 0, 0, 0);
        const days = Math.max(1, Math.round((dt.getTime() - td.getTime()) / 86400000));
        const deltaAbs = Math.abs(draft.targetWeightLbs - draft.currentWeightLbs);
        const rawPace = days > 0 ? deltaAbs / (days / 7) : 0;
        return rawPace > maxPace;
      })()
    : false;

  function fmtFromToday(ymd: string): string {
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    const target = new Date(ymd + "T00:00:00");
    const days = Math.round((target.getTime() - todayDate.getTime()) / 86400000);
    if (days <= 0) return "Today";
    if (days < 14) return days === 1 ? "in 1 day" : `in ${days} days`;
    const weeks = Math.round(days / 7);
    if (weeks < 9) return `in ${weeks} weeks`;
    const months = Math.round(days / 30);
    return `in ${months} months`;
  }

  const inCaution = draft.paceLbsPerWeekAbs >= cautionPace;

  return (
    <DesktopShell
      steps={DESKTOP_STEPS}
      currentStepIndex={3}
      onStepClick={onStepClick}
      isStepReachable={isStepReachable}
      eyebrow="Step 04 · Target"
      title={<>Where are you, and where to?</>}
      subtitle="Drag the rulers. Pick a pace, or pick a date."
      onBack={onBack}
      backLabel="Activity"
      onPrimary={onNext}
      primaryLabel="See my plan"

      onExit={onExit}
      headerRight={headerRight}
      preview={<PreviewProjection draft={draft} goal={goal} />}
      form={
        <div style={{ display: "grid", gap: 14, maxWidth: 680 }}>
          {/* Current weight */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Eyebrow>Current weight</Eyebrow>
              <SegmentedToggle
                size="sm"
                options={[
                  { value: "lb", label: "lbs" },
                  { value: "kg", label: "kg" },
                ]}
                value={draft.unitsWeight}
                onChange={(u) => setField("unitsWeight", u as WeightUnit)}
              />
            </div>
            <div style={{ marginTop: 8 }}>
              <RulerInput
                value={currentDisp}
                min={range.min}
                max={range.max}
                step={weightStep(draft.unitsWeight)}
                majorStep={draft.unitsWeight === "lb" ? 10 : 5}
                unit={draft.unitsWeight}
                decimals={draft.unitsWeight === "lb" ? 0 : 1}
                accent="blue"
                compact
                onChange={(v) => setField("currentWeightLbs", parseWeightToLbs(v, draft.unitsWeight))}
              />
            </div>
          </div>

          {/* Target weight — anchored on the current weight so the indicator's
              offset from center visualizes the delta. Loss tints the band
              green; gain uses a neutral gradient. The big number doubles as
              an editable input with [−] / [+] steppers for keyboard-first
              edits — dragging is no longer the only way to change the value. */}
          <div>
            <Eyebrow>Target weight</Eyebrow>
            <EditableWeight
              value={targetDisp}
              min={range.min}
              max={range.max}
              step={weightStep(draft.unitsWeight)}
              unit={draft.unitsWeight}
              decimals={draft.unitsWeight === "lb" ? 0 : 1}
              onChange={(v) =>
                setDraft((d) => {
                  const nextTarget = parseWeightToLbs(v, draft.unitsWeight);
                  const effective = inferGoalFromDelta(nextTarget - d.currentWeightLbs);
                  return {
                    ...d,
                    targetWeightLbs: nextTarget,
                    targetTouched: true,
                    paceLbsPerWeekAbs: defaultPaceLbsPerWeek(effective, d.currentWeightLbs, nextTarget),
                  };
                })
              }
            />
            <div style={{ marginTop: 8 }}>
              <RulerInput
                value={targetDisp}
                min={range.min}
                max={range.max}
                step={weightStep(draft.unitsWeight)}
                majorStep={draft.unitsWeight === "lb" ? 10 : 5}
                unit={draft.unitsWeight}
                decimals={draft.unitsWeight === "lb" ? 0 : 1}
                accent="lime"
                compact
                hideValue
                anchorValue={currentDisp}
                highlightRange={
                  targetDisp !== currentDisp
                    ? {
                        from: currentDisp,
                        to: targetDisp,
                        tone: targetDisp < currentDisp ? "loss" : "gain",
                      }
                    : undefined
                }
                onChange={(v) =>
                  setDraft((d) => {
                    const nextTarget = parseWeightToLbs(v, draft.unitsWeight);
                    // Use inferred direction so pace stays in sync with the
                    // actual delta even if the stored goal is "maintain".
                    const effective = inferGoalFromDelta(nextTarget - d.currentWeightLbs);
                    return {
                      ...d,
                      targetWeightLbs: nextTarget,
                      targetTouched: true,
                      paceLbsPerWeekAbs: defaultPaceLbsPerWeek(effective, d.currentWeightLbs, nextTarget),
                    };
                  })
                }
              />
            </div>
            <div style={{ marginTop: 6, textAlign: "center", color: T.fg2, fontSize: "0.86rem", fontWeight: 600, ...T.tabular }}>
              Net change{" "}
              <span style={{ color: mismatch ? T.warn : T.lime, fontWeight: 800 }}>
                {delta >= 0 ? "+" : "−"}
                {Math.abs(targetDisp - currentDisp).toFixed(draft.unitsWeight === "lb" ? 0 : 1)} {draft.unitsWeight}
              </span>
            </div>
            {mismatch ? (
              <div
                style={{
                  marginTop: 14,
                  padding: "10px 14px",
                  borderRadius: 12,
                  background: "rgba(245,158,11,0.10)",
                  border: "1px solid rgba(245,158,11,0.35)",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <div style={{ flex: 1, color: "#fde68a", fontSize: "0.84rem", lineHeight: 1.45, fontWeight: 600 }}>
                  Looks like a {inferred}. You picked {storedGoal ? GOAL_LABELS[storedGoal] : "—"}.
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setDraft((d) => ({
                      ...d,
                      goalDirection: inferred,
                      paceLbsPerWeekAbs: defaultPaceLbsPerWeek(inferred, d.currentWeightLbs, d.targetWeightLbs),
                    }))
                  }
                  style={{
                    padding: "8px 14px",
                    borderRadius: 999,
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    color: T.fg,
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  Switch to {GOAL_LABELS[inferred]}
                </button>
              </div>
            ) : null}
          </div>

          {/* Pace + Date toggle */}
          {goal !== "maintain" ? (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Eyebrow>Pace</Eyebrow>
                <SegmentedToggle
                  size="sm"
                  options={[
                    { value: "pace", label: "By pace" },
                    { value: "date", label: "By date" },
                  ]}
                  value={mode}
                  onChange={(m) => setMode(m as "pace" | "date")}
                />
              </div>
              {mode === "pace" ? (
                <>
                  <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "nowrap" }}>
                    {presets.map((p) => (
                      <Chip
                        key={p.preset}
                        active={draft.pacePreset === p.preset}
                        caution={p.caution}
                        onClick={() => setPace(p.lbsPerWeek, p.preset)}
                      >
                        {p.label} · {(+p.lbsPerWeek.toFixed(2)).toString()}
                      </Chip>
                    ))}
                  </div>
                  <PaceSlider
                    value={draft.paceLbsPerWeekAbs}
                    min={0.1}
                    max={maxPace}
                    step={0.05}
                    cautionFrom={cautionPace}
                    showAxisLabels
                    onChange={(v) => setPace(v)}
                  />
                  {inCaution ? (
                    <div
                      style={{
                        marginTop: 8,
                        padding: "8px 12px",
                        borderRadius: 10,
                        background: "rgba(245,158,11,0.10)",
                        border: "1px solid rgba(245,158,11,0.35)",
                        color: "#fde68a",
                        fontSize: "0.78rem",
                        lineHeight: 1.4,
                      }}
                    >
                      {goal === "gain"
                        ? `Above ${cautionPace.toFixed(1)} lb/wk is mostly fat. Capping at the safety floor.`
                        : `Above ${cautionPace.toFixed(1)} lb/wk is hard to sustain. Capping at the safety floor.`}
                    </div>
                  ) : null}
                </>
              ) : (
                <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
                  <DateField
                    value={dateYmdLocal || computedEndYmd || todayStr}
                    min={minYmd}
                    max={maxYmd}
                    onChange={setEndDate}
                    placeholder="Pick a target date"
                  />
                  <div style={{ color: T.fg2, fontSize: "0.84rem", fontWeight: 600, marginTop: 2 }}>
                    {fmtFromToday(dateYmdLocal || computedEndYmd || todayStr)}
                  </div>
                  {dateClamp && computedEndYmd ? (
                    <div
                      style={{
                        padding: "10px 14px",
                        borderRadius: 12,
                        background: "rgba(245,158,11,0.10)",
                        border: "1px solid rgba(245,158,11,0.35)",
                        color: "#fde68a",
                        fontSize: "0.84rem",
                        lineHeight: 1.5,
                      }}
                    >
                      To hit that date you'd need to eat below the safety floor. We've slowed
                      the plan to a safe pace; goal reached{" "}
                      <span style={{ fontWeight: 800 }}>{fmtDate(computedEndYmd)}</span> instead.
                    </div>
                  ) : null}
                </div>
              )}

              <div
                style={{
                  marginTop: 16,
                  padding: "12px 16px",
                  borderRadius: 12,
                  background: "rgba(255,255,255,0.025)",
                  border: `1px solid ${T.hairline}`,
                  borderLeft: `3px solid ${T.lime}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 14,
                }}
              >
                <div>
                  <Eyebrow>Hits goal</Eyebrow>
                  <div style={{ marginTop: 4, fontSize: "1.1rem", fontWeight: 800, ...T.tabular, letterSpacing: "-0.01em" }}>
                    {computedEndYmd ? fmtDate(computedEndYmd) : "—"}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <Eyebrow>Pace</Eyebrow>
                  <div style={{ marginTop: 4, fontSize: "1.1rem", fontWeight: 800, ...T.tabular, letterSpacing: "-0.01em" }}>
                    {goal === "lose" ? "−" : "+"}
                    {draft.paceLbsPerWeekAbs.toFixed(2)}
                    <span style={{ fontSize: "0.7rem", color: T.muted, marginLeft: 4 }}>lb/wk</span>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      }
    />
  );
}

// ── Step 05 — Plan reveal + Sign-in on one page ─────────────────────────

function DesktopPlanAndAuthStep({
  draft,
  goal,
  isAuthenticated,
  onBack,
  onComplete,
  onExit,
  onStepClick,
  isStepReachable,
}: {
  draft: OnboardingDraft;
  goal: GoalDirection;
  isAuthenticated: boolean;
  onBack: () => void;
  onComplete: (choice: AuthChoice) => Promise<void> | void;
  onExit?: () => void;
  onStepClick?: (i: number) => void;
  isStepReachable?: (i: number) => boolean;
}): React.ReactElement {
  const [submitting, setSubmitting] = useState<null | "apple" | "google" | "magic">(null);
  const [magicSent, setMagicSent] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState("");

  const { bmr, tdee, dailyTarget, endYmd, sprintName } = useMemo(() => {
    const sex = draft.sex ?? "X";
    const b = bmrFor({ weightLbs: draft.currentWeightLbs, heightIn: draft.heightIn, sex, birthday: draft.birthday }) ?? 0;
    const t = b > 0 ? tdeeFor(b, draft.activityFactor) : 0;
    const target = t > 0 ? dailyTargetFor({ goal, paceLbsPerWeek: draft.paceLbsPerWeekAbs, tdee: t, sex }) : 0;
    const effPace = t > 0
      ? effectivePaceLbsPerWeek({ paceLbsPerWeekAbs: draft.paceLbsPerWeekAbs, tdee: t, sex, goal })
      : draft.paceLbsPerWeekAbs;
    const end =
      goal === "maintain"
        ? ""
        : projectedEndDateYmd(draft.currentWeightLbs, draft.targetWeightLbs, effPace);
    return { bmr: b, tdee: t, dailyTarget: target, endYmd: end, sprintName: autoSprintName(goal, end || new Date().toISOString().slice(0, 10)) };
  }, [draft, goal]);

  const sign = draft.targetWeightLbs > draft.currentWeightLbs ? "+" : "−";

  return (
    <DesktopShell
      steps={DESKTOP_STEPS}
      currentStepIndex={4}
      onStepClick={onStepClick}
      isStepReachable={isStepReachable}
      hidePreview
      eyebrow={isAuthenticated ? "Step 05 · Your plan" : "Step 05 · Save your plan"}
      title={<>Your plan is ready.</>}
      subtitle={isAuthenticated
        ? "Your sprint is synced to your account. Start tracking when you're ready."
        : "Sign in to sync this sprint across devices, or keep it local on this one. You can sign in later."}
      onBack={onBack}
      backLabel="Target"
      onPrimary={isAuthenticated ? () => onComplete({ kind: "already-authed" }) : undefined}
      primaryLabel={isAuthenticated ? "Start tracking" : undefined}
      footerHint={isAuthenticated ? undefined : "You can finish setup without signing in."}
      onExit={onExit}
      form={
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isAuthenticated ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)",
            gap: 64,
            width: "100%",
            maxWidth: isAuthenticated ? 720 : 1180,
            margin: isAuthenticated ? "0 auto" : undefined,
          }}
        >
          {isAuthenticated ? null : (
          /* Left: auth options */
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Eyebrow>Sign in</Eyebrow>
            <div style={{ display: "grid", gap: 10, marginTop: 14, maxWidth: 420 }}>
              <Btn
                kind="white"
                size="lg"
                full
                onClick={async () => {
                  setSubmitting("apple");
                  try {
                    await onComplete({ kind: "apple" });
                  } finally {
                    setSubmitting(null);
                  }
                }}
                disabled={!!submitting}
                icon={
                  <svg width="18" height="22" viewBox="0 0 384 512" fill="currentColor">
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                  </svg>
                }
              >
                Continue with Apple
              </Btn>
              <Btn
                kind="secondary"
                size="lg"
                full
                onClick={async () => {
                  setSubmitting("google");
                  try {
                    await onComplete({ kind: "google" });
                  } finally {
                    setSubmitting(null);
                  }
                }}
                disabled={!!submitting}
                icon={
                  <svg width="18" height="18" viewBox="0 0 48 48" fill="none">
                    <path d="M44 24c0-1.4-.1-2.7-.4-4H24v8h11.3c-.5 2.6-2 4.8-4.2 6.3v5.2h6.8C41.8 35.9 44 30.4 44 24z" fill="#4285F4" />
                    <path d="M24 44c5.7 0 10.4-1.9 13.9-5.1l-6.8-5.2c-1.9 1.3-4.3 2-7.1 2-5.5 0-10.1-3.7-11.7-8.7H5.2v5.3C8.7 39.4 15.8 44 24 44z" fill="#34A853" />
                    <path d="M12.3 27.1c-.4-1.2-.6-2.5-.6-3.8s.2-2.6.6-3.8v-5.3H5.2C3.8 17.1 3 20.4 3 24s.8 6.9 2.2 9.9l7.1-6.8z" fill="#FBBC04" />
                    <path d="M24 11.5c3.1 0 5.8 1.1 8 3.1l6-6C34.4 5.1 29.7 3 24 3 15.8 3 8.7 7.6 5.2 14.2l7.1 5.3c1.6-5 6.2-8 11.7-8z" fill="#EA4335" />
                  </svg>
                }
              >
                Continue with Google
              </Btn>
              {emailOpen ? (
                <div
                  style={{
                    padding: "14px",
                    borderRadius: 12,
                    background: "rgba(255,255,255,0.04)",
                    border: `1px solid ${T.hairlineStrong}`,
                  }}
                >
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    style={{
                      width: "100%",
                      height: 44,
                      padding: "0 14px",
                      borderRadius: 10,
                      background: "rgba(255,255,255,0.04)",
                      border: `1px solid ${T.hairlineStrong}`,
                      color: T.fg,
                      fontSize: "0.96rem",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  <div style={{ marginTop: 10 }}>
                    <Btn
                      kind="primary"
                      size="md"
                      full
                      disabled={!/^\S+@\S+\.\S+$/.test(email) || !!submitting}
                      onClick={async () => {
                        setSubmitting("magic");
                        try {
                          await onComplete({ kind: "magic", email });
                          setMagicSent(true);
                        } finally {
                          setSubmitting(null);
                        }
                      }}
                    >
                      Email me a sign-in link
                    </Btn>
                  </div>
                </div>
              ) : (
                <Btn kind="secondary" size="lg" full onClick={() => setEmailOpen(true)}>
                  Continue with email
                </Btn>
              )}
              {magicSent ? (
                <div
                  style={{
                    marginTop: 10,
                    padding: "12px 14px",
                    borderRadius: 12,
                    background: "rgba(34,197,94,0.10)",
                    border: "1px solid rgba(34,197,94,0.30)",
                    color: "#bbf7d0",
                    fontSize: "0.84rem",
                    fontWeight: 600,
                    lineHeight: 1.5,
                  }}
                >
                  Check your inbox — we sent a sign-in link to {email}.
                </div>
              ) : null}
            </div>
            <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-start" }}>
              <button
                type="button"
                onClick={() => onComplete({ kind: "skip" })}
                style={{
                  background: "transparent",
                  border: "none",
                  color: T.muted,
                  fontSize: "0.86rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: "8px 0",
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
                  textDecorationColor: "rgba(255,255,255,0.22)",
                  letterSpacing: "-0.005em",
                }}
              >
                Continue without signing in →
              </button>
            </div>
            <div style={{ marginTop: 20, color: T.muted, fontSize: "0.74rem", lineHeight: 1.5 }}>
              By continuing you agree to the{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noreferrer"
                style={{ color: T.fg2, textDecoration: "underline", textDecorationColor: "rgba(255,255,255,0.22)", textUnderlineOffset: 3 }}
              >
                Terms
              </a>{" "}
              and{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noreferrer"
                style={{ color: T.fg2, textDecoration: "underline", textDecorationColor: "rgba(255,255,255,0.22)", textUnderlineOffset: 3 }}
              >
                Privacy Policy
              </a>.
            </div>
          </div>
          )}

          {/* Right (or only) column: plan reveal card */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Eyebrow>Your plan</Eyebrow>
            <div
              style={{
                marginTop: 14,
                background: T.bgCard,
                border: `1px solid ${T.hairline}`,
                borderRadius: 22,
                padding: "26px 28px",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "5px 10px",
                  borderRadius: 999,
                  background: "rgba(132,204,22,0.10)",
                  border: "1px solid rgba(132,204,22,0.32)",
                  color: "#d9f99d",
                  fontSize: "0.74rem",
                  fontWeight: 800,
                }}
              >
                <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: T.lime }} />
                Sprint 01 · {sprintName}
              </div>
              <div style={{ marginTop: 14 }}>
                <Eyebrow>Daily intake</Eyebrow>
                <div
                  style={{
                    fontSize: "3.4rem",
                    fontWeight: 700,
                    letterSpacing: "-0.03em",
                    lineHeight: 1,
                    marginTop: 6,
                    ...T.tabular,
                    background: T.ctaGrad,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {dailyTarget.toLocaleString()}
                  <span style={{ fontSize: "1.1rem", color: T.muted, fontWeight: 700, marginLeft: 10, background: "none", WebkitTextFillColor: T.muted }}>kcal</span>
                </div>
                {dailyTarget > 0 && dailyTarget < ((draft.sex ?? "X") === "F" ? 1200 : 1500) ? (
                  <div style={{ marginTop: 8 }}>
                    <span style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "0.18rem 0.5rem",
                      borderRadius: 999,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      color: "rgba(253,224,71,0.95)",
                      border: "1px solid rgba(251,191,36,0.45)",
                      letterSpacing: "0.02em",
                    }}>Low intake</span>
                  </div>
                ) : null}
                {goal === "maintain" ? (
                  <div style={{ marginTop: 8, color: T.fg2, fontWeight: 600, fontSize: "0.92rem" }}>
                    Maintaining at{" "}
                    <span style={{ color: T.fg, fontWeight: 800 }}>
                      {displayWeight(draft.currentWeightLbs, draft.unitsWeight)} {draft.unitsWeight}
                    </span>
                  </div>
                ) : endYmd ? (
                  <div style={{ marginTop: 8, color: T.fg2, fontWeight: 600, fontSize: "0.92rem" }}>
                    Hits goal on <span style={{ color: T.fg, fontWeight: 800 }}>{fmtDate(endYmd)}</span>
                  </div>
                ) : null}
              </div>

              {/* Projection chart — slope reflects pace */}
              {(() => {
                const { y0, y1 } = chartEndpointsForPlan(goal, draft.paceLbsPerWeekAbs);
                return (
                  <div style={{ position: "relative", marginTop: 18, height: 122 }}>
                    <svg viewBox="0 0 320 110" width="100%" height="100" preserveAspectRatio="none" style={{ display: "block" }}>
                      <defs>
                        <linearGradient id="pg-desktop-plan-projection" x1="0" x2="0" y1="0" y2="1">
                          <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.22" />
                          <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      {[18, 55, 92].map((y) => (
                        <line key={y} x1="0" y1={y} x2="320" y2={y} stroke="rgba(255,255,255,0.06)" strokeDasharray="2 6" />
                      ))}
                      <path d={`M0,${y0} L316,${y1} L320,${y1} L320,110 L0,110 Z`} fill="url(#pg-desktop-plan-projection)" />
                      <path d={`M0,${y0} L316,${y1}`} fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" />
                      <circle cx="0" cy={y0} r="4.5" fill="#fff" />
                      <circle cx="316" cy={y1} r="5.5" fill="#84CC16" />
                    </svg>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: 4,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: T.muted,
                        ...T.tabular,
                        letterSpacing: "-0.005em",
                      }}
                    >
                      <span>
                        Today · {displayWeight(draft.currentWeightLbs, draft.unitsWeight)} {draft.unitsWeight}
                      </span>
                      <span style={{ color: "#a3e635" }}>
                        {endYmd
                          ? `${new Date(endYmd + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })} · ${displayWeight(draft.targetWeightLbs, draft.unitsWeight)} ${draft.unitsWeight}`
                          : `Holding · ${displayWeight(draft.currentWeightLbs, draft.unitsWeight)} ${draft.unitsWeight}`}
                      </span>
                    </div>
                  </div>
                );
              })()}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 18 }}>
                {[
                  { l: "Pace", v: goal === "maintain" ? "0.0" : `${sign}${draft.paceLbsPerWeekAbs.toFixed(1)}`, s: "lb/wk" },
                  { l: "Net change", v: goal === "maintain" ? "0.0" : `${sign}${Math.abs(draft.targetWeightLbs - draft.currentWeightLbs).toFixed(1)}`, s: "lb" },
                  { l: "TDEE", v: tdee > 0 ? Math.round(tdee).toLocaleString() : "—", s: "kcal" },
                ].map((k) => (
                  <div
                    key={k.l}
                    style={{
                      background: "rgba(255,255,255,0.025)",
                      border: `1px solid ${T.hairline}`,
                      borderRadius: 12,
                      padding: "10px 12px",
                    }}
                  >
                    <Eyebrow sz="0.6rem">{k.l}</Eyebrow>
                    <div style={{ fontSize: "1.0rem", fontWeight: 800, ...T.tabular, marginTop: 4, letterSpacing: "-0.01em", color: T.fg }}>
                      {k.v}
                      <span style={{ fontSize: "0.62rem", color: T.muted, fontWeight: 700, marginLeft: 4 }}>{k.s}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 14, color: T.muted, fontSize: "0.72rem", textAlign: "center", lineHeight: 1.5 }}>
                Resting burn ≈ {bmr > 0 ? Math.round(bmr).toLocaleString() : "—"} kcal/day · {draft.activityFactor.toFixed(2)}× activity
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
}

// ── Small helpers ──────────────────────────────────────────────────────

function Eyebrow({ children, sz = "0.7rem" }: { children: React.ReactNode; sz?: string }): React.ReactElement {
  return (
    <div style={{ fontSize: sz, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.muted }}>
      {children}
    </div>
  );
}

// Editable weight display for the Target step. Renders [−] <big number> [+]
// where the number flips to a focused <input> on click. Steppers + Enter both
// commit; Escape reverts. Out-of-range entries clamp to [min, max].
function EditableWeight({
  value,
  min,
  max,
  step,
  unit,
  decimals,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  decimals: number;
  onChange: (next: number) => void;
}): React.ReactElement {
  const [editing, setEditing] = useState(false);
  const [draftStr, setDraftStr] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  function clamp(n: number): number {
    return Math.min(max, Math.max(min, +(Math.round(n / step) * step).toFixed(decimals + 2)));
  }
  function commit(): void {
    const parsed = parseFloat(draftStr);
    if (Number.isFinite(parsed)) {
      const c = clamp(parsed);
      if (c !== value) onChange(c);
    }
    setEditing(false);
  }
  function startEdit(): void {
    setDraftStr(value.toFixed(decimals));
    setEditing(true);
    // focus + select after render
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  }

  const stepperStyle: React.CSSProperties = {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: `1px solid ${T.hairlineStrong}`,
    background: "rgba(255,255,255,0.04)",
    color: T.fg,
    fontSize: "1.2rem",
    fontWeight: 700,
    lineHeight: 1,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    fontFamily: "inherit",
    transition: `background ${T.motion}, border-color ${T.motion}`,
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 14,
        marginTop: 4,
      }}
    >
      <button
        type="button"
        onClick={() => onChange(clamp(value - step))}
        disabled={value <= min}
        aria-label={`Decrease by ${step} ${unit}`}
        style={{ ...stepperStyle, opacity: value <= min ? 0.4 : 1, cursor: value <= min ? "not-allowed" : "pointer" }}
      >
        −
      </button>

      {editing ? (
        <input
          ref={inputRef}
          type="number"
          inputMode="decimal"
          step={step}
          min={min}
          max={max}
          value={draftStr}
          onChange={(e) => setDraftStr(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            else if (e.key === "Escape") setEditing(false);
          }}
          style={{
            width: 130,
            textAlign: "center",
            fontSize: "2.0rem",
            fontWeight: 700,
            letterSpacing: "-0.035em",
            color: T.fg,
            background: "rgba(255,255,255,0.06)",
            border: `1px solid ${T.lime}`,
            borderRadius: 10,
            padding: "4px 8px",
            outline: "none",
            fontFamily: "inherit",
            ...T.tabular,
          }}
        />
      ) : (
        <button
          type="button"
          onClick={startEdit}
          aria-label="Edit target weight"
          style={{
            background: "transparent",
            border: "1px dashed transparent",
            borderRadius: 10,
            padding: "4px 10px",
            cursor: "text",
            color: T.fg,
            fontFamily: "inherit",
            transition: `border-color ${T.motion}`,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = T.hairlineStrong)}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = "transparent")}
        >
          <span
            style={{
              fontSize: "2.0rem",
              fontWeight: 700,
              letterSpacing: "-0.035em",
              ...T.tabular,
              lineHeight: 1,
            }}
          >
            {value.toFixed(decimals)}
          </span>
          <span style={{ fontSize: "0.9rem", color: T.muted, fontWeight: 700, marginLeft: 8 }}>{unit}</span>
        </button>
      )}

      <button
        type="button"
        onClick={() => onChange(clamp(value + step))}
        disabled={value >= max}
        aria-label={`Increase by ${step} ${unit}`}
        style={{ ...stepperStyle, opacity: value >= max ? 0.4 : 1, cursor: value >= max ? "not-allowed" : "pointer" }}
      >
        +
      </button>
    </div>
  );
}
